import is from '@sindresorhus/is';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
    AgentChatMessage,
    AwaitingCallback,
    CallbackActionInput,
    FrontendContext,
    UserMessage,
} from '../../data-contract/chat';
import { chatKeys, useChatThread as useChatThreadQuery, useSubmitChatInteractionCallback } from '../api';
import {
    appendContextRefreshEvent,
    appendStructuredEvent,
    appendTextChunk,
    type MessagesInfiniteData,
    prependUserMessage,
    setAssistantMessageStatus,
    upsertAssistantMessage,
} from '../messages-cache';
import { useChatStream } from './use-chat-stream';
import { useChatThread } from './use-chat-thread';

export type TurnStatus = 'complete' | 'error' | 'idle' | 'streaming';

export type ChatTurnState = {
    readonly status: TurnStatus;
    readonly messageId: string | null;
    readonly errorMessage: string | null;
};

export type SendOptions = {
    readonly message: string;
    readonly frontendContext?: FrontendContext;
};

export type SubmitCallbackAnswerOptions = {
    readonly actions: readonly CallbackActionInput[];
    readonly blockId: string;
    readonly eventId: string;
};

export type ChatConversation = {
    readonly awaitingCallback: AwaitingCallback | null;
    readonly clearTurn: () => void;
    readonly isFreshThread: boolean;
    readonly isCreatingThread: boolean;
    readonly resumeTurn: () => Promise<void>;
    readonly selectThread: (id: string) => void;
    readonly send: (options: SendOptions) => Promise<void>;
    readonly startNewThread: () => void;
    readonly submitCallbackAnswer: (options: SubmitCallbackAnswerOptions) => void;
    readonly stop: () => void;
    readonly threadId: string | null;
    readonly threadName: string | null;
    readonly turn: ChatTurnState;
};

const INITIAL_TURN: ChatTurnState = { errorMessage: null, messageId: null, status: 'idle' };

const generateId = (): string =>
    is.function(crypto.randomUUID) ? crypto.randomUUID() : `local-${Date.now()}-${Math.random()}`;

const buildSendBody = (message: string, frontendContext: FrontendContext | undefined): UserMessage =>
    is.undefined(frontendContext) ? { message, type: 'new' } : { context: frontendContext, message, type: 'new' };

export const useChatConversation = (): ChatConversation => {
    const queryClient = useQueryClient();
    const {
        ensureThreadId,
        isCreatingThread,
        isFreshThread,
        resetThread,
        selectThread: setActiveThread,
        threadId,
    } = useChatThread();
    const [turn, setTurn] = useState<ChatTurnState>(INITIAL_TURN);
    const submitCallback = useSubmitChatInteractionCallback();

    const streamThreadIdRef = useRef<string | null>(null);
    const currentMessageIdRef = useRef<string | null>(null);
    const activeThreadIdRef = useRef<string | null>(threadId);
    activeThreadIdRef.current = threadId;

    const threadQuery = useChatThreadQuery(threadId ?? undefined);
    const pendingTurn = threadQuery.data?.pendingTurn;
    const awaitingCallback = pendingTurn?.awaitingCallback ?? null;
    const threadName = threadQuery.data?.name ?? null;

    const updateMessages = useCallback(
        (
            streamThreadId: string,
            updater: (data: MessagesInfiniteData | undefined) => MessagesInfiniteData | undefined,
        ): void => {
            queryClient.setQueriesData<MessagesInfiniteData>({ queryKey: chatKeys.messages(streamThreadId) }, updater);
        },
        [queryClient],
    );

    const stream = useChatStream({
        onChunk: (text) => {
            const tid = streamThreadIdRef.current;
            const messageId = currentMessageIdRef.current;
            if (is.null(tid) || is.null(messageId)) return;
            updateMessages(tid, (data) => appendTextChunk(data, messageId, text));
        },
        onComplete: () => {
            const tid = streamThreadIdRef.current;
            const messageId = currentMessageIdRef.current;
            if (!is.null(tid) && !is.null(messageId)) {
                updateMessages(tid, (data) => setAssistantMessageStatus(data, messageId, 'complete'));
            }
            setTurn((prev) => ({ ...prev, status: 'complete' }));
        },
        onConnectionLost: () => {
            const tid = streamThreadIdRef.current;
            const messageId = currentMessageIdRef.current;
            if (!is.null(tid) && !is.null(messageId)) {
                updateMessages(tid, (data) => setAssistantMessageStatus(data, messageId, 'error'));
            }
            setTurn((prev) => ({ ...prev, errorMessage: prev.errorMessage ?? 'Connection lost', status: 'error' }));
        },
        onError: (event) => {
            const tid = streamThreadIdRef.current;
            const messageId = currentMessageIdRef.current;
            if (!is.null(tid) && !is.null(messageId)) {
                updateMessages(tid, (data) => setAssistantMessageStatus(data, messageId, 'error'));
            }
            setTurn((prev) => ({ ...prev, errorMessage: event.payload.message, status: 'error' }));
        },
        onMessageStart: (event) => {
            const tid = streamThreadIdRef.current;
            currentMessageIdRef.current = event.messageId;
            if (!is.null(tid)) {
                updateMessages(tid, (data) => upsertAssistantMessage(data, event.messageId, event.timestamp));
            }
            setTurn((prev) => ({ ...prev, messageId: event.messageId }));
        },
        onStructuredResponse: (event) => {
            const tid = streamThreadIdRef.current;
            const messageId = currentMessageIdRef.current;
            if (is.null(tid) || is.null(messageId)) return;
            updateMessages(tid, (data) => appendStructuredEvent(data, messageId, event));
        },
        onContextRefresh: (event) => {
            const tid = streamThreadIdRef.current;
            const messageId = currentMessageIdRef.current;
            if (is.null(tid) || is.null(messageId)) return;
            updateMessages(tid, (data) => appendContextRefreshEvent(data, messageId, event));
        },
    });

    const startTurn = useCallback(
        (streamThreadId: string, body: UserMessage): void => {
            streamThreadIdRef.current = streamThreadId;
            currentMessageIdRef.current = null;
            setTurn({ errorMessage: null, messageId: null, status: 'streaming' });
            stream.connect(streamThreadId, body);
        },
        [stream],
    );

    const send = useCallback(
        async ({ frontendContext, message }: SendOptions): Promise<void> => {
            const trimmed = message.trim();
            if (trimmed.length === 0) return;

            const tid = await ensureThreadId();
            const userMessage: AgentChatMessage = {
                createdAt: new Date().toISOString(),
                id: generateId(),
                role: 'user',
                text: trimmed,
            };
            updateMessages(tid, (data) => prependUserMessage(data, userMessage));
            startTurn(tid, buildSendBody(trimmed, frontendContext));
        },
        [ensureThreadId, startTurn, updateMessages],
    );

    const resumeTurn = useCallback(async (): Promise<void> => {
        if (is.null(threadId)) return;
        startTurn(threadId, { resume: true, type: 'resume' });
    }, [threadId, startTurn]);

    const submitCallbackAnswer = useCallback(
        ({ actions, blockId, eventId }: SubmitCallbackAnswerOptions): void => {
            const hasLiveStream = turn.status === 'streaming';
            const submittedThreadId = threadId;
            submitCallback.mutate(
                { blockId, eventId, payload: { actions: [...actions] } },
                {
                    onSuccess: () => {
                        // A fresh proposal is answered over the still-open stream: the suspended
                        // gate waiter wakes and streams the continuation. After a thread switch
                        // that stream is torn down, so the decision is recorded server-side but
                        // nothing resumes the turn. Resume here so the gate's fast-path replays
                        // the recorded decision and streams the continuation.
                        //
                        // Guard against a mid-flight thread switch: this closure and resumeTurn
                        // are bound to the thread that was active at submit time, so resuming
                        // after the user moved on would resurrect the wrong thread's stream.
                        if (hasLiveStream) return;
                        if (activeThreadIdRef.current !== submittedThreadId) return;
                        void resumeTurn();
                    },
                },
            );
        },
        [submitCallback, turn.status, threadId, resumeTurn],
    );

    const stop = useCallback((): void => {
        stream.disconnect();
        setTurn(INITIAL_TURN);
    }, [stream]);

    const selectThread = useCallback(
        (id: string): void => {
            stream.disconnect();
            setTurn(INITIAL_TURN);
            setActiveThread(id);
        },
        [stream, setActiveThread],
    );

    const startNewThread = useCallback((): void => {
        stream.disconnect();
        setTurn(INITIAL_TURN);
        resetThread();
    }, [stream, resetThread]);

    const clearTurn = useCallback((): void => setTurn(INITIAL_TURN), []);

    const hasResumedRef = useRef(false);
    useEffect(() => {
        if (is.null(threadId) || is.nullOrUndefined(pendingTurn)) return;
        if (!is.nullOrUndefined(pendingTurn.awaitingCallback)) return;
        if (turn.status === 'streaming') return;
        if (hasResumedRef.current) return;
        hasResumedRef.current = true;
        void resumeTurn();
    }, [threadId, pendingTurn, turn.status, resumeTurn]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: resets the resume-once latch whenever the active thread changes.
    useEffect(() => {
        hasResumedRef.current = false;
    }, [threadId]);

    return useMemo(
        () => ({
            awaitingCallback,
            clearTurn,
            isCreatingThread,
            isFreshThread,
            resumeTurn,
            selectThread,
            send,
            startNewThread,
            stop,
            submitCallbackAnswer,
            threadId,
            threadName,
            turn,
        }),
        [
            awaitingCallback,
            clearTurn,
            isCreatingThread,
            isFreshThread,
            resumeTurn,
            selectThread,
            send,
            startNewThread,
            stop,
            submitCallbackAnswer,
            threadId,
            threadName,
            turn,
        ],
    );
};
